import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import StickyQuoteBar from '../../components/feature/StickyQuoteBar';

export default function DSCRLoansPage() {
  const ref1 = useRef(null);
  const inView1 = useInView(ref1, { once: true, margin: '-100px' });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <StickyQuoteBar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-block px-4 py-2 bg-yellow-500 text-gray-900 rounded-full text-sm font-semibold mb-6">
                <i className="ri-bank-line mr-2"></i>
                Complete DSCR Loan Guide
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Everything You Need to Know About <span className="text-yellow-500">DSCR Loans</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                The comprehensive guide to understanding, qualifying for, and securing DSCR loans for your investment property portfolio.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a href="/apply" className="px-8 py-4 bg-yellow-500 text-gray-900 rounded-lg font-semibold hover:bg-yellow-400 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer">
                  <i className="ri-file-text-line mr-2"></i>
                  Apply Now
                </a>
                <a href="/dscr-loan-basics" className="px-8 py-4 bg-white/10 text-white rounded-lg font-semibold hover:bg-white/20 transition-all duration-300 backdrop-blur-sm whitespace-nowrap cursor-pointer">
                  <i className="ri-book-open-line mr-2"></i>
                  Learn the Basics
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What is DSCR Loan Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              ref={ref1}
              initial={{ opacity: 0, x: -30 }}
              animate={inView1 ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-block px-4 py-2 bg-yellow-500 text-gray-900 rounded-full text-sm font-semibold mb-6">
                <i className="ri-lightbulb-line mr-2"></i>
                The Basics Explained
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Understanding DSCR Loans: The Investor's <span className="text-yellow-600">Financing Solution</span>
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                <strong className="text-gray-900">What is a DSCR loan?</strong> A DSCR loan (Debt Service Coverage Ratio loan) is a specialized mortgage product designed for real estate investors who want to finance investment properties based on the property's rental income rather than personal income verification.
              </p>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Unlike traditional mortgages that require extensive personal income documentation, DSCR loans evaluate your property's ability to generate rental income that covers the monthly mortgage payment. This makes them ideal for self-employed investors, those with complex tax returns, or anyone seeking a streamlined qualification process.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-4 p-4 bg-white rounded-xl border-l-4 border-yellow-500 shadow-sm">
                  <div className="flex items-center justify-center w-12 h-12 bg-yellow-100 rounded-lg flex-shrink-0">
                    <i className="ri-file-list-3-line text-2xl text-yellow-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">No Income Verification</h4>
                    <p className="text-gray-600 text-sm">No need for tax returns, W-2s, or pay stubs. Your property's income qualifies you.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 bg-white rounded-xl border-l-4 border-yellow-500 shadow-sm">
                  <div className="flex items-center justify-center w-12 h-12 bg-yellow-100 rounded-lg flex-shrink-0">
                    <i className="ri-home-gear-line text-2xl text-yellow-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Property Cash Flow Focus</h4>
                    <p className="text-gray-600 text-sm">Qualification based on rental income potential, not your personal finances.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 bg-white rounded-xl border-l-4 border-yellow-500 shadow-sm">
                  <div className="flex items-center justify-center w-12 h-12 bg-yellow-100 rounded-lg flex-shrink-0">
                    <i className="ri-building-line text-2xl text-yellow-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Unlimited Properties</h4>
                    <p className="text-gray-600 text-sm">Build your portfolio without restrictions on the number of financed properties.</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              ref={ref1}
              initial={{ opacity: 0, x: 30 }}
              animate={inView1 ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              {/* Main Image Card */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://static.readdy.ai/image/cd984ab7ba08a0f8b6f75adc7751930e/76fd81fc06f39256c5860648c547924c.jpeg"
                  alt="Understanding DSCR Loans"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/40 via-transparent to-transparent"></div>
              </div>

              {/* Floating Stats Card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-2xl p-6 border-t-4 border-yellow-500 max-w-xs">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl">
                    <i className="ri-calculator-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-gray-900">1.25</div>
                    <div className="text-sm text-gray-600">Minimum DSCR Ratio</div>
                  </div>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed">
                  Property income should be 125% of monthly mortgage payment for optimal approval odds.
                </p>
              </div>

              {/* Floating Badge */}
              <div className="absolute -top-4 -right-4 bg-gradient-to-br from-gray-900 to-gray-800 text-white rounded-2xl shadow-2xl p-4 transform rotate-3">
                <div className="flex items-center space-x-2">
                  <i className="ri-flashlight-line text-yellow-500 text-xl"></i>
                  <span className="font-bold text-sm">Fast Approval</span>
                </div>
                <p className="text-xs text-gray-300 mt-1">Close in 21-30 days</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}